import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { CustomButton, Error, Loading } from '../_common';
import {
    AddEquipmentsInput,
    GetEquipmentsInfo_getEquipmentsInfo as EquipmentsInfo,
    GetAllEquipmentTypes,
    GetAllLocations,
    GetEquipmentsByAssetType,
} from '../../types/schemaTypes';
import './AddEquipment.scss';
import { Col, Modal } from 'react-bootstrap';
import { GET_ALL_EQUIPMENT_TYPES, GET_ALL_LOCATIONS, GET_EQUIPMENTS_BY_ASSET_TYPE } from '../../graphql/client/queries';
import SearchableDropdown, { DropdownOption } from '../_common/SearchableDropdown/SearchableDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MultiSelectDropdownV2 from '../_common/MultiSelectDropdown/MultiSelectDropdownV2';
import Select from 'react-select';
import MultiSelectDropdownV3 from '../_common/MultiSelectDropdown/MultiSelectDropdownV3';

interface AddEquipmentModalProps {
    asset?: AddEquipmentsInput;
    equipmentsInfo?: EquipmentsInfo | undefined;
    showModal: boolean;
    loading: boolean;
    onClickSave: (valuesToAddEquipment: AddEquipmentsInput) => void;
    error: ErrorLike | undefined;
    onClickCancel: () => void;
}

interface OptionType {
    label: string;
    value: string;
}

const AddEquipmentModal = ({
    asset,
    loading,
    onClickCancel,
    onClickSave,
    showModal,
}: AddEquipmentModalProps): React.ReactElement => {
    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;
    const [selectedLocationType, setSelectedLocationType] = useState<string>();
    const [assetType, setAssetType] = useState(asset?.assetType ?? '');
    const [selectedLocationId, setSelectedLocationId] = useState<number | null>();
    const [selectedLocation, setSelectedLocation] = useState<string>();
    const [selectedAssetIds, setSelectedAssetIds] = useState('');
    const [selectedAssetTypeId, setSelectedAssetTypeId] = useState(0);
    const [error, setError] = useState('');
    const initialSelectedAssets: OptionType[] = [];
    const [selectedAssets, setSelectedAssets] = useState(initialSelectedAssets);
    const [selectedSingleAsset, setSelectedSingleAsset] = useState(null);

    useEffect(() => {
        setSelectedAssetIds('');
    }, [assetType]);

    useEffect(() => {
        setSelectedLocation('');
    }, [selectedLocationType]);

    const handleOnSave = async () => {
        const valuesToAddEquipment: AddEquipmentsInput = {
            assetType: assetType,
            assetTypeId: selectedAssetTypeId,
            assetIds: selectedAssetIds,
            locationType: selectedLocationType,
            locationId: selectedLocationId,
            location: selectedLocation,
        };
        onClickSave(valuesToAddEquipment);
    };

    const asssetTypes: DropdownOption[] = [];
    const { data: addEquipmentData, loading: addEquipmentLoading } =
        useQuery<GetAllEquipmentTypes>(GET_ALL_EQUIPMENT_TYPES);

    if (addEquipmentData && addEquipmentData.getAllEquipmentTypes) {
        addEquipmentData.getAllEquipmentTypes.map((item) => {
            if (item && item.name !== 'Mats' && item.name !== 'Forklift Ramp' && item.name !== 'Miscellaneous') asssetTypes.push(item);
        });
    }

    const originDestinationTypeDropdownOptions: DropdownOption[] = [
        { id: 1, name: 'Job' },
        { id: 2, name: 'Lofa' },
        { id: 3, name: 'Yard' },
    ];

    const locationsDropdownOptions: DropdownOption[] = [];

    const { data: locationsData } = useQuery<GetAllLocations>(GET_ALL_LOCATIONS, {
        variables: {
            input: {
                locationType: selectedLocationType,
                locationId: 0,
            },
        },
        fetchPolicy: 'network-only',
        skip: !selectedLocationType,
    });

    if (locationsData && locationsData.getAllLocations) {
        locationsData.getAllLocations.map((item) => {
            if (item && item.locationType === selectedLocationType && item.locationId && item.descrip) {
                locationsDropdownOptions.push({
                    id: item.locationId,
                    name: item.descrip,
                });
            }
        });
    }

    const { data: addEquipmentAssetData, loading: addEquipmentAssetLoading, refetch:refetchEquipments } = useQuery<GetEquipmentsByAssetType>(
        GET_EQUIPMENTS_BY_ASSET_TYPE,
        {
            variables: {
                assetType: assetType,
            },
            skip:!assetType
        },
    );

    const equipmentDescripList: OptionType[] = [];
    if (addEquipmentAssetData && addEquipmentAssetData.getEquipmentsByAssetType) {
        addEquipmentAssetData.getEquipmentsByAssetType.forEach((item) => {
            if (item && item.id && item.name) {
                equipmentDescripList.push({
                    value: item.id.toString(),
                    label: item.name,
                });
            }
        });
    }

    useEffect(()=>{
        if(assetType){
            refetchEquipments();

        }
    },[assetType,refetchEquipments])

    const isSaveButtonDisabled = () => {
        let isDisabled = false;
        if (!assetType || assetType.length === 0) isDisabled = true;
        if (!selectedAssetIds || selectedAssetIds.length < 1) isDisabled = true;
        if (!selectedLocationType || selectedLocationType.length === 0) isDisabled = true;
        if (!selectedLocation) isDisabled = true;
        return isDisabled;
    };

    const handleOnChangeAssetType = (assetTypeId: number) => {
        setError('');
        let assetType = '';
        asssetTypes.map((item) => {
            if (assetTypeId === item.id) assetType = item.name.toString();
        });
        setAssetType(assetType);
        setSelectedAssetTypeId(assetTypeId);
        setSelectedAssetIds('');
    };

    const handleOnChangeAssetDescrip = (selectedAsset): void => {
        const selectedAssetValues: number[] = [];
        if (selectedAsset && selectedAsset !== null && Array.isArray(selectedAsset)) {
            setSelectedAssets(selectedAsset);
            for (let i = 0; i < selectedAsset.length; i++) {
                selectedAssetValues.push(parseInt(selectedAsset[i].value));
            }
            setSelectedAssetIds(selectedAssetValues.toString());
        } else {
            setSelectedSingleAsset(selectedAsset.label);
            setSelectedAssetIds(selectedAsset.value.toString());
        }
    };

    const handleOnChangeLocationType = (value: number) => {
        let locationTerminalType = '';
        originDestinationTypeDropdownOptions.map((item) => {
            if (item.id === value) locationTerminalType = item.name.toString();
        });
        setSelectedLocationType(locationTerminalType);
    };

    const handleOnChangeLocation = (locationTerminalId: number | null) => {
        let locationTerminal = '';

        locationsDropdownOptions.map((item) => {
            if (item.id === locationTerminalId) locationTerminal = item.name.toString();
        });
        setSelectedLocation(locationTerminal);
        setSelectedLocationId(locationTerminalId);
    };
    
    return (
        <Modal show={showModal} onHide={onClickCancel} dialogClassName="AddEquipment-Container">
            <Modal.Header className="AddEquipmentModal-Header">
                <div className="AddEquipmentModal-Header-Text ">Add Equipments</div>
                <i className="material-icons AddEquipmentModal-Close-Icon" onClick={onClickCancel}>
                    clear
                </i>
            </Modal.Header>
            <Modal.Body className="AddAsset-Body">
                <div className="AddEquipmentModal-Upper-TextField-Container">
                    <div className="AddEquipmentModal-Down-TextField-Container">
                        <SearchableDropdown
                            dropdownOptions={asssetTypes}
                            id="equipmentType"
                            label="Equipment Type"
                            onSelect={(value) => {
                                value && handleOnChangeAssetType(parseInt(value));
                            }}
                            value={assetType}
                            required
                        />

                        {assetType=='Sandbox' ? (<Col md={5} className="AddEquipmentDetails-Field">
                            <MultiSelectDropdownV3
                                dropdownOptions={equipmentDescripList}
                                label="Equipment"
                                onChange={(value: OptionType): void => {
                                    handleOnChangeAssetDescrip(value);
                                }}
                                key={`my_unique_select_key__${JSON.stringify(selectedAssets)}`}
                                value={selectedAssets}
                            />
                        </Col>):(
                            <Col md={5} className="AddEquipmentDetails-Field">
                                <div className="SearchableDropdown">
                                <label className={`Searchable-Dropdown-Label`}>Equipment</label>
                                <Select
                                    options={equipmentDescripList}
                                    isMulti={false}
                                    //key={`my_unique_select_key__${JSON.stringify(selectedAssets)}`}
                                    value={selectedSingleAsset}
                                    onChange={(value) => {
                                        handleOnChangeAssetDescrip(value);
                                    }}
                                    placeholder={selectedSingleAsset? selectedSingleAsset:''}
                                />
                                </div>
                            </Col>
                        )}
                    </div>
                </div>
                <div className="AddEquipmentModal-Upper-TextField-Container">
                    <div className="AddEquipmentModal-Down-TextField-Container">
                        <SearchableDropdown
                            dropdownOptions={originDestinationTypeDropdownOptions}
                            id="locationType"
                            label="Location Type"
                            onSelect={(value) => {
                                value && handleOnChangeLocationType(parseInt(value));
                            }}
                            value={selectedLocationType}
                            required
                        />

                        <Col md={5} className="AddEquipmentDetails-Field-Location">
                            <SearchableDropdown
                                dropdownOptions={locationsDropdownOptions}
                                id="location"
                                label="Location"
                                onSelect={(value) => {
                                    value && handleOnChangeLocation(parseInt(value));
                                }}
                                value={selectedLocation}
                                required
                                key={selectedLocationType}
                                autoCompleteEnabled
                                endIcon={searchIcon}
                            />
                        </Col>
                    </div>
                </div>

                <Modal.Footer>
                    <div className="AddAsset-HeaderButtonsContainer">
                        <div className="AddAsset-CancelButton">
                            <CustomButton title="Cancel" onClick={onClickCancel} isBordered />
                        </div>
                        <CustomButton title="Save" onClick={handleOnSave} isDisabled={isSaveButtonDisabled()} />
                    </div>
                </Modal.Footer>
            </Modal.Body>
        </Modal>
    );
};

export default AddEquipmentModal;

